/**
 * Pure-JS device signing for origins without WebCrypto. `crypto.subtle` is
 * undefined on a plain-http LAN origin (the server-hosted app at
 * http://192.168.x.x:41400/web/), so the device key there is a software
 * Ed25519 key: the same proof format, the same OKP public JWK, verified by
 * the broker with no change. The private key is a plain byte array in
 * IndexedDB — extractable by nature, which is the accepted trade-off of the
 * LAN-HTTP mode (Plex's Preferred semantics).
 *
 * Only the synchronous noble API is used: `signAsync`/`getPublicKeyAsync`
 * would reach for `crypto.subtle` and throw on exactly these origins.
 */
import * as ed from "@noble/ed25519";
import { sha256, sha512 } from "@noble/hashes/sha2.js";
import { b64uEncode, type PublicJwk } from "@rago/web-shared";

ed.hashes.sha512 = sha512;

export interface SoftwareKey {
  priv: Uint8Array;
  publicJwk: PublicJwk;
}

export function generateSoftwareKey(): SoftwareKey {
  const priv = ed.utils.randomSecretKey();
  const pub = ed.getPublicKey(priv);
  return { priv, publicJwk: { kty: "OKP", crv: "Ed25519", x: b64uEncode(pub) } };
}

export function softwareSign(priv: Uint8Array, data: Uint8Array): Uint8Array {
  return ed.sign(data, priv);
}

export function sha256Bytes(data: Uint8Array): Uint8Array {
  return sha256(data);
}

/** RFC 4122 v4 from getRandomValues (crypto.randomUUID needs a secure
 * context). Version and variant nibbles set, so `z.uuid()` accepts it. */
export function uuidV4(): string {
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
